import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';
import { Formik } from "formik";
import { API_BASE_URL } from '../../config';
import endpoints from '../../services/endpoints';
import api from '../../api/api.js';

// Components
import Loader2 from '../../components/loader2';
import HomeUserProfilePureCSS from "../../profiles/HomeProfilePureCSS";
import BusinessProfilePureCSS from "../../profiles/BusinessProfilePureCSS";
import ModernNavbarPureCSS from "../../components/Navbar/ModernNavBarPureCSS";
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import { checkExistingQuote } from '../../components/Message/Services/checkExistingQuote';

// UI Components (From your library)
import { Input } from "../../_newprofiles/components/ui/Input"
import { Separator } from "../../_newprofiles/components/ui/Separator"
import { Textarea } from "../../_newprofiles/components/ui/Textarea"
import { Button } from "../../_newprofiles/components/ui/Button" // Using for modal only

// Icons
import {
  Clock, DollarSign, MapPin, User, MessageSquare, ArrowLeft,
  Search, Calendar, Tag, Filter, ChevronRight, Briefcase,
  Film, FileText, Download, Play, Camera, CheckCircle
} from "lucide-react";

// NEW STYLES
import styles from "./BusinessJobsDashboardPureCSS.module.css";
import quoteModalStyles from "./QuoteModal.module.css";
import customSelectStyles from "./BusinessJobsDashboardPureCSS.module.css"; // Reusing for image modal

// --- HELPER COMPONENT FOR FORMIK EFFECT ---
const QuoteFormEffect = ({ values, setFieldValue, quotesTemplate, selectedJob, currentUser }) => {
    React.useEffect(() => {
        if (values.templateId && currentUser) {
            const selectedTemplate = quotesTemplate.find(t => t.id.toString() === values.templateId);
            if (selectedTemplate) {
                let message = selectedTemplate.templateMessage;
                const replacements = {
                    '\\[Client Name\\]': `${selectedJob?.homeUserFirstName || ''} ${selectedJob?.homeUserLastName || ''}`.trim(),
                    '\\[address\\]': selectedJob?.address || selectedJob?.suburb || selectedJob?.postcode || '',
                    '\\[phone\\]': currentUser?.phone || '',
                    '\\[Business Name\\]': currentUser?.businessName || '',
                    '\\[Your Name\\]': currentUser?.name || '',
                    '\\[ABN\\]': currentUser?.abn || '',
                };
                for (const placeholder in replacements) {
                    message = message.replace(new RegExp(placeholder, 'g'), replacements[placeholder]);
                }
                const quoteValue = values.price ? `$${values.price}` : '[quote]';
                message = message.replace(/\[quote\]/g, quoteValue);
                setFieldValue('message', message, false);
            }
        }
    }, [values.price, values.templateId, selectedJob, currentUser, quotesTemplate, setFieldValue]);
    return null;
};

const BusinessJobsDashboardPureCSS = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobCardRefs = useRef({});
  
  // State
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [businessUserId, setBusinessUserId] = useState(null);
  const [username, setUsername] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });
  const [statusFilter, setStatusFilter] = useState("default");
  const [showAssignedOnly, setShowAssignedOnly] = useState(false);

  // Logic State
  const [isQuoteSent, setIsQuoteSent] = useState(false);
  const [isSendQuotePopup, setIsSendQuotePopup] = useState(false);
  const [quotesTemplate, setQuoteTemplate] = useState([]);
  const [isLoadingSuccess, setIsLoadingSuccess] = useState(false);
  
  // UI State
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [modalMedia, setModalMedia] = useState([]);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [isJobDetailsVisible, setIsJobDetailsVisible] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1024);

  // --- RESIZE HANDLER ---
  const handleResize = useCallback(() => {
    setIsMobileView(window.innerWidth <= 1024);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // --- INITIALIZATION ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodeToken = jwtDecode(token);
      const businessId = decodeToken.BusinessId;
      setBusinessUserId(businessId);
      setUsername(decodeToken.UserName || 'User');

      fetchJobs(businessId);
      getAllQuoteTemplate();
      fetchBusinessProfile(businessId);
    }
  }, []);

  // --- API CALLS ---
  const fetchBusinessProfile = async (businessId) => {
      try {
          const response = await api.get(`/api/BusinessProfile/getBusinessProfile?businessId=${businessId}&logView=false&ommitProfilePic=true`);
          const apiData = response.data;
          const primaryLocation = apiData.locations?.find(loc => loc.isPrimary === true) || apiData.locations?.[0];
          const contactPhone = primaryLocation ? (primaryLocation.mobile || primaryLocation.landline || '') : '';
          
          setCurrentUser({
              name: apiData.businesses?.businessUserName || apiData.businesses?.businessName || '', 
              businessName: apiData.businesses?.businessName || '',
              phone: contactPhone,
              abn: apiData.businesses?.abn || '',
          });
      } catch (error) {
          console.error("Failed to fetch business profile:", error);
          setCurrentUser({ name: 'Your Name', businessName: 'Your Business', phone: 'Your Phone', abn: 'Your ABN' });
      }
  };

  const fetchJobs = async (businessId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/Jobs/getMyJobsForBusiness?businessId=${businessId}`);
      if (response.data !== 'Subscription Expired !!') {
        const fetchedJobs = response.data;
        setJobs(fetchedJobs);
        setFilteredJobs(fetchedJobs);
        
        // Handle pre-selection (Priority: URL Param > Navigation State > Default First)
        const paramJobId = searchParams.get('jobId');
        
        if (paramJobId) {
            const jobToSelect = fetchedJobs.find(j => String(j.job_id) === String(paramJobId));
            if (jobToSelect) {
                console.log("jobToSelect = ", jobToSelect);
                setSelectedJob(jobToSelect);
                setIsJobDetailsVisible(true);

            }
        } else if (location.state?.selectedJob) {
          const jobToSelect = fetchedJobs.find(j => j.job_id === location.state.selectedJob.job_id);
          if (jobToSelect) {
            setSelectedJob(jobToSelect);
            setIsJobDetailsVisible(true);
            setSearchParams({ jobId: jobToSelect.job_id });

            checkQuoted(jobToSelect, businessId); 

          } else if (fetchedJobs.length > 0) {
             // Only default select on desktop
             if (!isMobileView) {
                 setSelectedJob(fetchedJobs[0]);
                 setSearchParams({ jobId: fetchedJobs[0].job_id });
                 checkQuoted(fetchedJobs[0], businessId);
             }
          }
        } else if (fetchedJobs.length > 0 && !isMobileView) {
          setSelectedJob(fetchedJobs[0]);
          setSearchParams({ jobId: fetchedJobs[0].job_id });
          checkQuoted(fetchedJobs[0], businessId);
        }
      } else {
        setJobs([]);
        setFilteredJobs([]);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllQuoteTemplate = async () => {
      try {
          const response = await api.get(`/${endpoints.getQuoteTemplate}`);
          setQuoteTemplate(response.data);
      } catch (error) {
          setError(error.message);
      }
  };

  // --- ACTIONS ---
  const handleJobClick = async (job) => {
    setSelectedJob(job);
    setSearchParams({ jobId: job.job_id });
    setIsJobDetailsVisible(true);
    
    // Mobile scroll reset
    if (isMobileView) {
        window.scrollTo(0,0);
    }
  };


  
  useEffect(() => {
    const verifyQuoteStatus = async () => {
      if (selectedJob && businessUserId) {
        // Reset state slightly to ensure UI doesn't lag with old data
        // Optional: setIsQuoteSent(false); 
        
        try {
          const response = await checkExistingQuote(selectedJob.job_id, businessUserId);
          console.log("Auto-checking quote for job:", selectedJob.job_id, "Result:", response);
          setIsQuoteSent(response);
        } catch (err) {
          console.error("Error checking quote status:", err);
        }
      }
    };

    verifyQuoteStatus();
  }, [selectedJob, businessUserId]); 


  const handleBackToList = () => {
    setIsJobDetailsVisible(false);
    setSelectedJob(null);
  };

  const handleMessageClient = async (e, selectedJob) => {
    try {
        console.log("selectedJob:", selectedJob);
        const loggedInBusinessUserId = localStorage.getItem('loggedInBusinessUserId');
        const senderBusinessUserId = loggedInBusinessUserId;
        const userId = selectedJob.userId;

        const jobUserType = selectedJob.userType;

        // Default, the owner of the job is Home User
        var url = `senderBusinessUserId=${senderBusinessUserId}&homeUserId=${userId}`;

        // Check if business user, and change url accordingly
        if (jobUserType == "BusinessUser")
        {
            url = `senderBusinessUserId=${senderBusinessUserId}&receiverBusinessUserId=${userId}`;
        }

        const { data } = await axios.get(`${API_BASE_URL}/api/Chat/NewConversation?${url}`);

        window.open(`/business_messages?conversationId=${data.conversationId}`, '_blank', 'noopener,noreferrer');

    } catch (err) {

        console.error('Error starting conversation:', err);
    }
  };


  // Update this function
const checkQuoted = async (job, manualBusinessId = null) => {
    // Use the manual ID if passed, otherwise fall back to the state variable
    const idToCheck = manualBusinessId || businessUserId;

    if (job && idToCheck) {
      // Use idToCheck here, NOT businessUserId
      const response = await checkExistingQuote(job.job_id, idToCheck);
      console.log("Checking quote for ID:", idToCheck);
      setIsQuoteSent(response);
    }
};
  

  const SendQuote = async (values) => {
      setIsLoading(true);
      setIsLoadingSuccess(false);
      const loggedInBusinessId = localStorage.getItem('loggedInBusinessId');
      const loggedInBusinessUserId = localStorage.getItem('loggedInBusinessUserId');

      let params = {
          jobId: selectedJob.job_id,
          businessUserId: loggedInBusinessUserId,
          businessId: loggedInBusinessId,
          homeUserId: selectedJob.userId,
          jobOwnerType: selectedJob.userType,
          price: values?.price,
          message: values?.message,
          templateId: values?.templateId || 0
      }

      try {
          await axios.post(`${API_BASE_URL}/${endpoints.sendQuote}${selectedJob.job_id}/quote`, params);
          setTimeout(() => {
              setIsLoading(false);
              setIsLoadingSuccess(true);
              Swal.fire({
                  icon: 'success',
                  title: 'Quote Sent !!',
                  timer: 1500,
                  showConfirmButton: false,
                  customClass: "confirmation-popup"
              }).then(() => {
                  setIsSendQuotePopup(false);
                  setIsQuoteSent(true);
              });
          }, 500);
      } catch (error) {
          setIsLoading(false);
          setIsLoadingSuccess(false);
          setError(error.message);
      }
  };

  // --- FILTERING & SORTING ---
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    let itemsToFilter = [...jobs];

    if (showAssignedOnly) {
        itemsToFilter = itemsToFilter.filter(job => job.assignedBusinessId == businessUserId);
    }

    // 2. Handle Status Dropdown (The Main Fix)
    if (statusFilter === "default") {
        // DEFAULT VIEW: Show Open/Active jobs.
        // CRITICAL: Explicitly HIDE "Completed" jobs here, even if they come from backend.
        // We only want to see them if the user specifically selects "Completed" or "All".
        itemsToFilter = itemsToFilter.filter(job => 
            job.jobStatus !== "Archived" && 
            job.jobStatus !== "Completed" && 
            job.jobStatus !== "Cancelled"
        );
    }
    else if (statusFilter === "All") {
        // Show everything except Archived (usually trash)
        itemsToFilter = itemsToFilter.filter(job => job.jobStatus !== "Archived");
    }
    else if (statusFilter === "Quoted") {
        // NEW LOGIC: Show jobs I have quoted on (excluding trash)
        itemsToFilter = itemsToFilter.filter(job => 
            job.myQuoteForJob !== null && 
            job.jobStatus !== "Archived"
        );
    }
    else {
        // Specific Filter (e.g., "Completed", "Open")
        itemsToFilter = itemsToFilter.filter(job => job.jobStatus === statusFilter);
    }


    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        itemsToFilter = itemsToFilter.filter(job =>
            (job.name && job.name.toLowerCase().includes(lower)) ||
            (job.description && job.description.toLowerCase().includes(lower)) ||
            (job.address && job.address.toLowerCase().includes(lower)) ||
            (job.postcode && job.postcode.toLowerCase().includes(lower))
        );
    }

    if (sortConfig.key !== null) {
        itemsToFilter.sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            if (sortConfig.key === 'created_at') { valA = new Date(a.created_at); valB = new Date(b.created_at); }
            else if (sortConfig.key === 'budgetAmount') { valA = Number(a.budgetAmount) || 0; valB = Number(b.budgetAmount) || 0; }
            else if (sortConfig.key === 'category') {
                valA = a.profession?.[0]?.name.toLowerCase() || '';
                valB = b.profession?.[0]?.name.toLowerCase() || '';
            }
            if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
    }
    setFilteredJobs(itemsToFilter);
  }, [jobs, searchTerm, sortConfig, statusFilter, showAssignedOnly]);

  // --- MEDIA HELPERS ---
  const getJobAttachments = (job) => {
    if (!job) return { images: [], videos: [], docs: [] };
    
    const rawPaths = [job.image1, job.image2, job.image3, job.image4].filter(Boolean);
    
    const processed = rawPaths.map(path => {
        let fullUrl = path.startsWith('http') ? path : `${API_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
        
        // Determine type based on extension
        const lower = fullUrl.toLowerCase();
        let type = 'document';
        if (/\.(jpg|jpeg|png|gif|webp)$/.test(lower)) type = 'image';
        else if (/\.(mp4|webm|mov)$/.test(lower)) type = 'video';
        
        return { url: fullUrl, type, name: path.split('/').pop() };
    });

    return {
        images: processed.filter(f => f.type === 'image'),
        videos: processed.filter(f => f.type === 'video'),
        docs: processed.filter(f => f.type === 'document')
    };
  };

  const openMediaModal = (mediaItems, index) => {
    setModalMedia(mediaItems);
    setSelectedImageIndex(index);
    setIsImageModalOpen(true);
  };
  const closeImageModal = () => setIsImageModalOpen(false);
  const nextImage = () => setSelectedImageIndex((prev) => (prev + 1) % modalMedia.length);
  const prevImage = () => setSelectedImageIndex((prev) => (prev - 1 + modalMedia.length) % modalMedia.length);

  // --- RENDER ---
  return (
    <div className={styles.dashboardContainer}>
        
        {/* Navigation */}
        <div className={styles.header}>
            <div className={styles.headerContent}>
                 <h1 className={styles.headerTitle}>Job Leads Dashboard</h1>
                 <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'jobs'} />
            </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContentContainer}>
            
            {/* Filter Toolbar */}
            <div className={styles.filterToolbar}>
                <div className={styles.searchInputWrapper}>
                    <Search className={styles.searchIcon} />
                    <input 
                        type="text" 
                        placeholder="Search by name, description, category..." 
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <div className={styles.filterActions}>
                    <button 
                        className={`${styles.filterBtn} ${showAssignedOnly ? styles.activeSort : ''}`} 
                        onClick={() => setShowAssignedOnly(!showAssignedOnly)}
                        style={showAssignedOnly ? { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#166534' } : {}}
                    >
                        <Briefcase size={16} /> Assigned
                    </button>

                    <Dropdown
                        placeholder="Status"
                        options={[
                          { value: "default", label: "Active" },
                          { value: "All", label: "All" },
                          { value: "Quoted", label: "Quoted" },
                          { value: "Open", label: "Open" },
                          { value: "Completed", label: "Completed" },
                        ]}
                        onSelect={setStatusFilter}
                        // Reuse generic styling or pass specific class
                    />
                    
                    <button 
                        className={`${styles.filterBtn} ${sortConfig.key === 'created_at' ? styles.activeSort : ''}`} 
                        onClick={() => handleSort('created_at')}
                    >
                        <Calendar size={16} /> Date
                    </button>
                    
                    <button 
                        className={`${styles.filterBtn} ${sortConfig.key === 'budgetAmount' ? styles.activeSort : ''}`} 
                        onClick={() => handleSort('budgetAmount')}
                    >
                        <DollarSign size={16} /> Budget
                    </button>
                    
                    <button 
                        className={`${styles.filterBtn} ${sortConfig.key === 'category' ? styles.activeSort : ''}`} 
                        onClick={() => handleSort('category')}
                    >
                        <Tag size={16} /> Category
                    </button>
                </div>
            </div>

            {/* Layout Grid */}
            <div className={`${styles.jobsLayout} ${isJobDetailsVisible ? styles.detailsVisible : ''}`}>

                {/* 1. Jobs List Column */}
                <div className={styles.jobsListColumn}>
                    {isLoading && <Loader2 />}
                    {!isLoading && error && <p className="text-red-500">Error: {error}</p>}
                    
                    {!isLoading && filteredJobs.map((job) => {
                        // --- LOGIC START: Determine Assignment Status ---
                        const isAssignedToMe = job.assignedBusinessId == businessUserId;
                        const isAssignedToOther = job.assignedBusinessId && !isAssignedToMe;

                        // Check if I have quoted on this job (and it's not assigned to anyone yet)
                        // Note: ensure your API returns 'myQuoteForJob' (camelCased usually in axios)
                        const isQuotedByMe = job.myQuoteForJob !== null && !isAssignedToMe && !isAssignedToOther;


                        // --- LOGIC END ---

                        return (
                            <div 
                                key={job.job_id}
                                ref={el => jobCardRefs.current[job.job_id] = el}
                                // Add the quoted class if applicable
                                className={`${styles.jobCard} ${selectedJob?.job_id === job.job_id ? styles.jobCardSelected : ''} ${isQuotedByMe ? styles.jobCardQuoted : ''}`}
                                onClick={() => handleJobClick(job)}
                                style={{

                                    // Case 1: Assigned to me (Green Side Bar + Green BG)
                                    ...(isAssignedToMe ? { 
                                        borderLeft: '4px solid #22c55e', // Green Side Bar
                                        backgroundColor: '#f0fdf4'       // Light Green Background
                                    } : {}),

                                    // Case 2: Assigned to other (Grey Side Bar + Grey BG)
                                    ...(isAssignedToOther ? { 
                                        borderLeft: '4px solid #cbd5e1', // Grey Side Bar
                                        backgroundColor: '#f8fafc',
                                        opacity: 0.7
                                    } : {}),


                                    // Case 3: Quoted by me (Orange)
                                    ...(isQuotedByMe ? {
                                        borderLeft: '4px solid #f97316', // Orange Left Border
                                        backgroundColor: '#fff7ed'       // Light Orange Background
                                    } : {})
                                }}
                            >
                                <div className={styles.jobHeader}>
                                    <h3 
                                        className={styles.jobTitle}
                                        style={isAssignedToOther ? { color: '#64748b' } : {}}
                                    >
                                        {job.name}
                                        {/* Only show Urgent badge if job is open and NOT quoted yet */}
                                        {job.deadline === "Urgently" && !isAssignedToOther && !isQuotedByMe && <div className={styles.urgentBadge}>Urgent</div>}
                                    </h3>
                                    <span className={styles.jobPrice}>
                                        {job.budgetAmount ? `$${job.budgetAmount}` : job.budget}
                                    </span>
                                </div>

                                <p className={styles.jobPreviewText}>{job.description}</p>

                                <div className={styles.jobMeta}>
                                    <div className={styles.metaItem}>
                                        <User size={14} /> 
                                        {`${job.homeUserFirstName} ${job.homeUserLastName}`}
                                    </div>
                                    <div className={styles.metaItem}>
                                        <MapPin size={14} />
                                        {job.address || job.suburb || job.postcode}
                                    </div>
                                    <div className={styles.metaItem}>
                                        <Clock size={14} />
                                        {new Date(job.created_at).toLocaleDateString()}
                                    </div>
                                </div>

                                {/* --- BADGES SECTION --- */}

                                {/* Badge: Assigned to ME */}
                                {isAssignedToMe && (
                                    <div className={styles.assignedBadge}>
                                        <Briefcase size={12} /> Assigned to you
                                    </div>
                                )}

                                {/* Badge: Quote Sent (New) */}
                                {isQuotedByMe && (
                                    <div className={styles.quotedBadge}>
                                        <CheckCircle size={12} /> Quote Sent
                                    </div>
                                )}

                                {/* Badge: Assigned to OTHER */}
                                {isAssignedToOther && (
                                    <div style={{
                                        marginTop: '10px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: '#e2e8f0', 
                                        color: '#64748b' 
                                    }}>
                                        <Briefcase size={12} /> Job Closed (Assigned)
                                    </div>
                                )}
                            </div>
                        );
                    })}     



                    {!isLoading && filteredJobs.length === 0 && (
                        <div className="text-center py-10 text-gray-500">No jobs found matching your criteria.</div>
                    )}

                </div>

                {/* 2. Job Details Column */}
                <div className={styles.jobDetailsColumn}>
                    {selectedJob ? (
                        <div className={styles.detailsCard}>
                            <div className={styles.detailsHeader}>
                                <div className={styles.detailsTitleBlock}>
                                    {isMobileView && (
                                        <button className={styles.mobileBackButton} onClick={handleBackToList}>
                                            <ArrowLeft size={24} />
                                        </button>
                                    )}
                                    <div>
                                        {selectedJob.assignedBusinessId == businessUserId && (
                                            <div className={styles.assignedBadge} style={{display:'inline-flex', marginBottom:'0.5rem'}}>
                                                <Briefcase size={12} /> Assigned to you
                                            </div>
                                        )}
                                        <h2>{selectedJob.name}</h2>
                                        <div className={styles.metaItem} style={{fontSize:'0.9rem', paddingTop: '0.5rem'}}>
                                            <MapPin size={16} /> {selectedJob.address || selectedJob.suburb || selectedJob.postcode}
                                        </div>
                                        <div className={styles.metaItem} style={{fontSize:'0.85rem', paddingTop: '0.5rem'}}>
                                            <Clock size={14} /> Posted {new Date(selectedJob.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                                
                                
<div className={styles.detailsActions}>
    <button className={`${styles.actionBtn} ${styles.btnSecondary}`} onClick={(e) => handleMessageClient(e, selectedJob)}>
        <MessageSquare size={18} /> Message
    </button>
    
    {(() => {
         const isAssignedToOther = selectedJob.assignedBusinessId && selectedJob.assignedBusinessId != businessUserId;

         if (isAssignedToOther) {
             return (
                 <button className={`${styles.actionBtn} ${styles.btnDisabled}`} disabled style={{backgroundColor:'#cbd5e1', color:'#475569', cursor:'not-allowed'}}>
                    <Briefcase size={16} style={{marginRight:5}}/> Job Assigned
                 </button>
             );
         }
         
         if (isQuoteSent) {
             return <button className={`${styles.actionBtn} ${styles.btnDisabled}`} disabled>Quote Sent</button>;
         }

         return (
             <button className={`${styles.actionBtn} ${styles.btnPrimary}`} onClick={() => setIsSendQuotePopup(true)}>
                 <DollarSign size={18} /> Quote Job
             </button>
         );
    })()}
</div>


                            </div>

                            <div className={styles.detailsContent}>
                                <div className={styles.detailSection}>
                                    <p className={styles.detailLabel}>Description</p>
                                    <p className={styles.detailValue}>{selectedJob.description}</p>
                                </div>

                                <Separator className="my-6"/>

                                <div className={styles.infoGrid}>
                                    <div>
                                        <p className={styles.detailLabel}>Client</p>
                                        <button onClick={() => {
                                            const isBusinessUser = selectedJob.userType === "BusinessUser";
                                            setSelectedUserId(isBusinessUser ? selectedJob.businessId : selectedJob.userId);
                                            setSelectedUserType(selectedJob.userType);
                                            setIsProfileOpen(true);
                                        }} className="text-blue-600 font-semibold hover:underline flex items-center gap-2">
                                            <User size={16}/> {selectedJob.homeUserFirstName} {selectedJob.homeUserLastName}
                                        </button>
                                    </div>
                                    <div>
                                        <p className={styles.detailLabel}>Budget</p>
                                        <p className={`${styles.detailValue} text-green-600 font-bold`}>
                                            {selectedJob.budgetAmount ? `$${selectedJob.budgetAmount}` : selectedJob.budget}
                                        </p>
                                    </div>
                                    <div>
                                        <p className={styles.detailLabel}>Deadline</p>
                                        <p className={styles.detailValue}>{selectedJob.deadline}</p>
                                    </div>
                                    <div>
                                        <p className={styles.detailLabel}>Site Visit</p>
                                        <p className={styles.detailValue}>{selectedJob.siteVisit}</p>
                                    </div>
                                </div>

                                {(() => {
                                    const { images, videos, docs } = getJobAttachments(selectedJob);
                                    const hasAttachments = images.length > 0 || videos.length > 0 || docs.length > 0;
                                    
                                    if (!hasAttachments) return null;

                                    return (
                                        <>
                                            <Separator className="my-6"/>
                                            
                                            {/* PHOTOS */}
                                            {images.length > 0 && (
                                                <div className={styles.detailSection}>
                                                    <p className={styles.detailLabel} style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                        <Camera size={16}/> Project Photos
                                                    </p>
                                                    <div className={styles.imageGrid}>
                                                        {images.map((img, idx) => (
                                                            <div 
                                                                key={`img-${idx}`} 
                                                                className={styles.thumbWrapper} 
                                                                onClick={() => openMediaModal([...images, ...videos], idx)}
                                                                style={{cursor:'pointer', borderRadius:'8px', overflow:'hidden', border:'1px solid #e2e8f0', aspectRatio:'1', position:'relative'}}
                                                            >
                                                                <img src={img.url} alt="Project" style={{width:'100%', height:'100%', objectFit:'cover'}} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* DOCUMENTS */}
                                            {docs.length > 0 && (
                                                <div className={styles.detailSection} style={{marginTop:'1.5rem'}}>
                                                    <p className={styles.detailLabel} style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                        <FileText size={16}/> Documents
                                                    </p>
                                                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                                                        {docs.map((doc, idx) => (
                                                            <div key={`doc-${idx}`} style={{
                                                                display:'flex', alignItems:'center', justifyContent:'space-between', 
                                                                padding:'12px', backgroundColor:'white', border:'1px solid #e2e8f0', borderRadius:'8px'
                                                            }}>
                                                                <div style={{display:'flex', alignItems:'center', gap:'12px', overflow:'hidden'}}>
                                                                    <div style={{padding:'8px', background:'#EFF6FF', borderRadius:'6px', color:'#2563EB'}}>
                                                                        <FileText size={20}/>
                                                                    </div>
                                                                    <span style={{fontWeight:'500', color:'#334155', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'200px'}}>
                                                                        {doc.name}
                                                                    </span>
                                                                </div>
                                                                <a 
                                                                    href={doc.url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className={styles.btnSmallOutline || "text-sm text-blue-600 border border-blue-200 px-3 py-1 rounded hover:bg-blue-50"}
                                                                    style={{textDecoration:'none', display:'flex', alignItems:'center', gap:'5px'}}
                                                                >
                                                                    <Download size={14}/> Download
                                                                </a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* VIDEOS */}
                                            {videos.length > 0 && (
                                                <div className={styles.detailSection} style={{marginTop:'1.5rem'}}>
                                                    <p className={styles.detailLabel} style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                        <Film size={16}/> Videos
                                                    </p>
                                                    <div className={styles.imageGrid}>
                                                        {videos.map((vid, idx) => (
                                                            <div 
                                                                key={`vid-${idx}`} 
                                                                className={styles.thumbWrapper} 
                                                                onClick={() => openMediaModal([...images, ...videos], images.length + idx)}
                                                                style={{cursor:'pointer', position:'relative', borderRadius:'8px', overflow:'hidden', backgroundColor:'#000', aspectRatio:'1'}}
                                                            >
                                                                <video src={vid.url} style={{width:'100%', height:'100%', objectFit:'cover', opacity:0.8}} />
                                                                <div style={{
                                                                    position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)',
                                                                    color:'white', background:'rgba(0,0,0,0.5)', borderRadius:'50%', padding:'10px'
                                                                }}>
                                                                    <Play size={24} fill="white" />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    ) : (
                        // Empty State for Details Panel
                        <div className={`${styles.detailsCard} flex flex-col items-center justify-center text-center p-10 h-full text-slate-400`}>
                            <Briefcase size={48} className="mb-4 opacity-50"/>
                            <h3 className="text-lg font-semibold text-slate-600">Select a job to view details</h3>
                            <p>Click on any job card from the list to see full requirements and send a quote.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* --- MODALS --- */}
{isSendQuotePopup && selectedJob && (
    <Modal isOpen={isSendQuotePopup} onClose={() => setIsSendQuotePopup(false)}>
        <div className="p-2"> {/* Optional padding container */}
            
            {/* 1. Header */}
            <div className={quoteModalStyles.modalHeader}>
                <h2 className={quoteModalStyles.title}>
                    <DollarSign className="text-amber-600" size={24} /> 
                    Submit Quote
                </h2>
                <p className={quoteModalStyles.subtitle}>Create a professional quote for this lead.</p>
            </div>

            {/* 2. Job Summary Card */}
            <div className={quoteModalStyles.jobSummary}>
                <div className={quoteModalStyles.jobSummaryTitle}>{selectedJob?.name}</div>
                <div className={quoteModalStyles.jobSummaryMeta}>
                    <MapPin size={14} /> {selectedJob?.address || selectedJob?.suburb || selectedJob?.postcode}
                    <span className="mx-2">•</span>
                    <span className="font-semibold text-slate-600">Client: {selectedJob?.homeUserFirstName}</span>
                </div>
            </div>
            
            {/* 3. Form */}
            <Formik
                initialValues={{ price: '', message: '', templateId: '' }}
                onSubmit={SendQuote}
                validate={(values) => {
                    const errors = {};
                    if (!values.price) errors.price = "Price is required";
                    if (!values.message) errors.message = "Message is required";
                    return errors;
                }}
            >
                {({ handleSubmit, handleChange, setFieldValue, values, errors, touched }) => (
                    <form onSubmit={handleSubmit}>
                        <QuoteFormEffect 
                            values={values}
                            setFieldValue={setFieldValue}
                            quotesTemplate={quotesTemplate}
                            selectedJob={selectedJob}
                            currentUser={currentUser}
                        />

                        <div className={quoteModalStyles.formGroup}>
                            <label htmlFor="price" className={quoteModalStyles.label}>Total Quote Amount (AUD)</label>
                            <div className={quoteModalStyles.inputWrapper}>
                                <span className={quoteModalStyles.currencySymbol}>$</span>
                                <Input 
                                    id="price" 
                                    name="price" 
                                    type="number" 
                                    placeholder="0.00" 
                                    className={quoteModalStyles.priceInput}
                                    value={values.price} 
                                    onChange={handleChange} 
                                />
                            </div>
                            {touched.price && errors.price && <p className={quoteModalStyles.error}>{errors.price}</p>}
                        </div>

                        <div className={quoteModalStyles.formGroup}>
                            <label htmlFor="templateId" className={quoteModalStyles.label}>Load Message Template (Optional)</label>
                            <select
                                id="templateId"
                                name="templateId"
                                value={values.templateId}
                                className={quoteModalStyles.selectInput}
                                onChange={(event) => {
                                    const templateId = event.target.value;
                                    handleChange(event);
                                    if (!templateId) setFieldValue("message", "");
                                }}
                            >
                                <option value="">-- Select a saved template --</option>
                                {quotesTemplate.map((data) => (
                                    <option key={data.id} value={data.id}>{data.templateName}</option>
                                ))}
                            </select>
                        </div>

                        <div className={quoteModalStyles.formGroup}>
                            <label htmlFor="message" className={quoteModalStyles.label}>Message to Client</label>
                            <Textarea 
                                id="message" 
                                name="message" 
                                value={values.message} 
                                onChange={handleChange} 
                                placeholder="Describe your services, availability, and what this quote includes..." 
                                rows={6} 
                                className={quoteModalStyles.textarea}
                            />
                            {touched.message && errors.message && <p className={quoteModalStyles.error}>{errors.message}</p>}
                        </div>

                        <div className={quoteModalStyles.modalFooter}>
                            <button type="button" className={quoteModalStyles.cancelBtn} onClick={() => setIsSendQuotePopup(false)}>
                                Cancel
                            </button>
                            <Button 
                                type="submit" 
                                className={quoteModalStyles.submitBtn}
                                isLoading={isLoading} 
                                isLoadingSuccess={isLoadingSuccess}
                            >
                                Send Quote
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    </Modal>
)}

        {isImageModalOpen && modalMedia.length > 0 && (
            <div className={styles.imageModalOverlay} onClick={closeImageModal} style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center'}}>
                <button onClick={closeImageModal} style={{position:'absolute', top:20, right:30, color:'white', background:'none', border:'none', fontSize:'2rem', cursor:'pointer'}}>&times;</button>
                
                {modalMedia.length > 1 && (
                    <button onClick={prevImage} style={{position:'absolute', left:20, color:'white', background:'none', border:'none', fontSize:'3rem', cursor:'pointer'}}>&#10094;</button>
                )}

                <div style={{maxWidth:'90%', maxHeight:'90vh', display:'flex', alignItems:'center', justifyContent:'center'}} onClick={(e) => e.stopPropagation()}>
                    {modalMedia[selectedImageIndex].type === 'video' ? (
                        <video 
                            src={modalMedia[selectedImageIndex].url} 
                            controls 
                            autoPlay 
                            style={{maxWidth:'100%', maxHeight:'90vh', objectFit:'contain'}}
                        />
                    ) : (
                        <img 
                            src={modalMedia[selectedImageIndex].url} 
                            alt="Enlarged" 
                            style={{maxWidth:'100%', maxHeight:'90vh', objectFit:'contain'}} 
                        />
                    )}
                </div>

                {modalMedia.length > 1 && (
                    <button onClick={nextImage} style={{position:'absolute', right:20, color:'white', background:'none', border:'none', fontSize:'3rem', cursor:'pointer'}}>&#10095;</button>
                )}
                
                <div style={{position:'absolute', bottom:20, color:'white', fontSize:'1rem'}}>
                    {selectedImageIndex + 1} / {modalMedia.length}
                </div>
            </div>
        )}

        {selectedUserType === "BusinessUser" ? (
            <BusinessProfilePureCSS
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                businessId={selectedUserId}
                useIDTYPE="BID"
                showMessageButton={false}
            />
        ) : (
            <HomeUserProfilePureCSS
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                userId={selectedUserId}
            />
        )}
    </div>
  );
};

export default BusinessJobsDashboardPureCSS;
